const fmt = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtPct = (n) => `${Number(n ?? 0).toFixed(1)}%`;
const fmtSigned = (n) => {
  const v = Number(n ?? 0);
  return `${v > 0 ? "+" : ""}${v.toFixed(1)}`;
};
const fmtSignedPct = (n) => {
  const v = Number(n ?? 0);
  return `${v > 0 ? "+" : ""}${v.toFixed(1)}%`;
};

export const formatTeacherCompact = (n) => {
  const v = Number(n ?? 0);
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return String(v);
};

export const buildTeacherTqiGaugeStyle = (tqi = 0) => {
  const pct = Math.min(100, Math.max(0, Number(tqi) || 0));
  const color = pct >= 60 ? "#10b981" : pct >= 35 ? "#7c3aed" : "#f59e0b";
  return {
    background: `radial-gradient(circle at center, white 0 58%, transparent 59%), conic-gradient(${color} 0 ${pct}%, rgba(124,58,237,.12) ${pct}% 100%)`,
    "--tex-gauge-color": color,
  };
};

export const buildTeacherHeroHeadline = (overview = {}) => {
  const tqi = overview.teacher_quality_index ?? 0;
  if (tqi < 35) return "Teacher quality is the primary improvement lever";
  if (tqi < 55) return "Workforce growth is positive, quality needs acceleration";
  return "Teacher workforce is scaling with improving quality signals";
};

export const buildTeacherHeroNarrative = (overview = {}) => {
  const growth = overview.teacher_growth ?? 0;
  const growthPct = overview.teacher_growth_pct ?? 0;
  const ctet = overview.ctet_coverage_pct ?? 0;
  const sign = growth >= 0 ? "increased" : "decreased";
  return `Teacher strength has ${sign} by ${fmt(Math.abs(growth))} (${fmtPct(growthPct)} YoY). CTET coverage stands at ${fmtPct(ctet)} — certification and training visibility should remain a standing review metric.`;
};

export const buildTeacherStatusChips = (overview = {}) => {
  const tqi = overview.teacher_quality_index ?? 0;
  const growth = overview.teacher_growth ?? 0;
  const ctet = overview.ctet_coverage_pct ?? 0;
  const chips = [];
  if (tqi < 40) chips.push({ label: "Quality Watch", className: "purple" });
  else if (tqi >= 55) chips.push({ label: "Quality Improving", className: "green" });
  else chips.push({ label: "Quality Moderate", className: "blue" });
  if (growth > 0) chips.push({ label: `+${fmt(growth)} Teachers`, className: "blue" });
  chips.push({ label: `CTET ${fmtPct(ctet)}`, className: ctet < 15 ? "amber" : "green" });
  return chips;
};

export const buildTeacherPrimaryAction = (overview = {}, blockData = []) => {
  const ctet = overview.ctet_coverage_pct ?? 0;
  const negative = (blockData || []).filter((b) => (b.teacher_growth_pct ?? 0) < 0);
  if (negative.length >= 3) {
    const names = negative
      .slice(0, 4)
      .map((b) => b.block_name)
      .join(", ");
    return `Run a 60-day block review for ${names}: align sanctioned posts, close vacancy gaps, and track weekly teacher strength until negative-growth blocks return to positive momentum.`;
  }
  if (ctet < 15) {
    return `Launch a district CTET acceleration sprint: set block-wise certification targets, publish weekly completion dashboards, and tie progress to the next executive review.`;
  }
  return `Sustain workforce momentum while elevating TQI: replicate high-growth block staffing practices and make CTET, CWSN and digital training completion standing agenda items.`;
};

export const buildTeacherExecutiveKpis = (overview = {}) => {
  const growth = overview.teacher_growth ?? 0;
  const growthPct = overview.teacher_growth_pct ?? 0;
  const tqi = overview.teacher_quality_index ?? 0;
  const risk = overview.teacher_risk_index ?? 0;
  const ctet = overview.ctet_coverage_pct ?? 0;
  return [
    {
      label: "Total Teachers",
      value: fmt(overview.teachers_cy),
      sub: growth >= 0 ? `+${fmt(growth)} CY growth` : `${fmt(growth)} CY change`,
      cardClass: "green",
      iconClass: "green",
    },
    {
      label: "Growth Rate",
      value: fmtPct(growthPct),
      sub: growthPct >= 0 ? "Positive workforce momentum" : "Declining workforce",
      cardClass: "teal",
      iconClass: "teal",
    },
    {
      label: "Avg / School",
      value: String(overview.avg_teachers_per_school ?? 0),
      sub: "Teacher availability density",
      cardClass: "purple",
      iconClass: "purple",
    },
    {
      label: "Quality Index",
      value: String(tqi),
      sub: tqi < 40 ? "Needs targeted improvement" : "On improvement path",
      cardClass: "purple",
      iconClass: "purple",
    },
    {
      label: "Risk Index",
      value: String(risk),
      sub: risk >= 8 ? "Elevated deployment risk" : "Monitor deployment gaps",
      cardClass: "amber",
      iconClass: "amber",
    },
    {
      label: "CTET Coverage",
      value: fmtPct(ctet),
      sub: ctet < 15 ? "Certification acceleration required" : "Certification progressing",
      cardClass: "green",
      iconClass: "green",
    },
  ];
};

export const buildTeacherInsightStrip = (overview = {}, schoolDistribution = []) => {
  const growthPct = overview.teacher_growth_pct ?? 0;
  const tqi = overview.teacher_quality_index ?? 0;
  const ctet = overview.ctet_coverage_pct ?? 0;
  const total = schoolDistribution.reduce((s, d) => s + (d.value ?? 0), 0);
  const noChange = schoolDistribution.find((d) => d.name === "No Change")?.value ?? 0;
  const noChangePct = total > 0 ? Math.round((noChange / total) * 100) : 0;

  return [
    {
      id: "workforce",
      label: "Workforce Base",
      title: growthPct >= 0 ? "Stable growth" : "Contraction watch",
      body: `Teacher count is ${growthPct >= 0 ? "growing" : "declining"} at the district level (${fmtPct(growthPct)} YoY). Total strength is ${fmt(overview.teachers_cy ?? 0)} across ${fmt(overview.total_schools ?? 0)} schools.`,
      iconClass: "blue",
    },
    {
      id: "staffing",
      label: "Staffing Change",
      title: `${noChangePct}% Unchanged`,
      body: `${fmt(noChange)} schools show no year-on-year staffing change. Movement is concentrated in schools with increased or decreased teacher counts.`,
      iconClass: "green",
    },
    {
      id: "quality",
      label: "Quality Risk",
      title: tqi < 40 ? "Low TQI" : "TQI watch",
      body: `Teacher Quality Index is ${tqi}. CTET, CWSN and digital readiness together drive this governance metric for block reviews.`,
      iconClass: "purple",
    },
    {
      id: "priority",
      label: "Priority Focus",
      title: ctet < 15 ? "CTET sprint" : "Training uplift",
      body: `CTET coverage at ${fmtPct(ctet)} remains the highest-leverage certification target for the next review cycle.`,
      iconClass: "amber",
    },
  ];
};

export const buildStaffingDonut = (schoolDistribution = [], totalSchools = 0) => {
  const decreased = schoolDistribution.find((d) => d.name === "Decreased Staffing")?.value ?? 0;
  const increased = schoolDistribution.find((d) => d.name === "Increased Staffing")?.value ?? 0;
  const noChange = schoolDistribution.find((d) => d.name === "No Change")?.value ?? 0;
  const total = decreased + increased + noChange || totalSchools || 1;

  const decPct = (decreased / total) * 100;
  const incPct = (increased / total) * 100;
  const noPct = (noChange / total) * 100;

  const seg1 = decPct;
  const seg2 = seg1 + incPct;

  return {
    total,
    segments: [
      { key: "decreased", label: "Decreased Staffing", value: decreased, color: "#ef4444", dotClass: "red" },
      { key: "increased", label: "Increased Staffing", value: increased, color: "#10b981", dotClass: "green" },
      { key: "no_change", label: "No Change", value: noChange, color: "#94a3b8", dotClass: "grey" },
    ],
    style: {
      background: `radial-gradient(circle at center, white 0 58%, transparent 59%), conic-gradient(#ef4444 0 ${seg1}%, #10b981 ${seg1}% ${seg2}%, #94a3b8 ${seg2}% 100%)`,
    },
  };
};

export const buildBlockPairedRows = (blockComparison = []) => {
  const rows = [...blockComparison].slice(0, 12);
  const maxVal = Math.max(...rows.flatMap((r) => [r.teachers_cy ?? 0, r.teachers_py ?? 0]), 1);
  return rows.map((row) => ({
    block: row.block_name,
    py: row.teachers_py ?? 0,
    cy: row.teachers_cy ?? 0,
    pyWidth: Math.round(((row.teachers_py ?? 0) / maxVal) * 100),
    cyWidth: Math.round(((row.teachers_cy ?? 0) / maxVal) * 100),
    displayCy: formatTeacherCompact(row.teachers_cy),
    displayPy: formatTeacherCompact(row.teachers_py),
  }));
};

export const buildGrowthRows = (blockData = []) => {
  const rows = [...blockData]
    .filter((b) => b.block_name)
    .sort((a, b) => (b.teachers_cy ?? 0) - (a.teachers_cy ?? 0))
    .slice(0, 14);
  const maxAbs = Math.max(...rows.map((r) => Math.abs(r.teacher_growth_pct ?? 0)), 1);
  return rows.map((row) => {
    const pct = row.teacher_growth_pct ?? 0;
    const width = Math.round((Math.abs(pct) / maxAbs) * 100);
    return {
      block: row.block_name,
      pct,
      display: fmtSigned(pct),
      width,
      positive: pct >= 0,
    };
  });
};

export const buildExecutiveActionQueue = (overview = {}, blockData = []) => {
  const negative = (blockData || []).filter((b) => (b.teacher_growth_pct ?? 0) < 0);
  const positive = (blockData || [])
    .filter((b) => (b.teacher_growth_pct ?? 0) > 3)
    .sort((a, b) => (b.teacher_growth_pct ?? 0) - (a.teacher_growth_pct ?? 0));
  const negNames = negative.map((b) => b.block_name).slice(0, 4).join(", ");
  const posNames = positive.map((b) => b.block_name).slice(0, 3).join(", ");
  const ctet = overview.ctet_coverage_pct ?? 0;

  return [
    {
      title: "Improve Teacher Qualification Visibility",
      body: `Make CTET completion (${fmtPct(ctet)}), training completion, and certification gap a standing block review metric.`,
      iconClass: "purple",
    },
    {
      title: "Review Negative-Growth Blocks",
      body: negNames
        ? `${negNames} should be reviewed for vacancies, transfers and sanctioned post alignment.`
        : "Monitor blocks with declining teacher strength for vacancy and transfer alignment.",
      iconClass: "red",
    },
    {
      title: "Replicate High-Growth Block Practices",
      body: posNames
        ? `${posNames} show strong growth and can provide staffing playbooks for weaker blocks.`
        : "Identify top-growing blocks and document staffing practices for district replication.",
      iconClass: "green",
    },
  ];
};

const tqiStatus = (block) => {
  const qi = block.quality_index ?? 0;
  const growth = block.teacher_growth_pct ?? 0;
  if (growth < -1 || qi < 25) return { label: "At Risk", className: "danger" };
  if (growth > 3 && qi >= 30) return { label: "Improving", className: "good" };
  return { label: "Watch", className: "warn" };
};

const riskLabel = (block) => {
  const ri = block.risk_index ?? 0;
  if (ri >= 8) return "High";
  if (ri >= 5) return "Medium";
  return "Low";
};

const followUp = (block) => {
  const growth = block.teacher_growth_pct ?? 0;
  if (growth > 5) return "Use as growth benchmark";
  if (growth < -1) return "Vacancy and transfer audit";
  if ((block.quality_index ?? 0) < 30) return "CTET and training sprint";
  return "Monitor monthly strength";
};

export const buildBlockWatchlist = (blockData = []) => {
  const sorted = [...blockData]
    .filter((b) => b.block_name)
    .sort((a, b) => {
      const aNeg = (a.teacher_growth_pct ?? 0) < 0 ? 1 : 0;
      const bNeg = (b.teacher_growth_pct ?? 0) < 0 ? 1 : 0;
      if (aNeg !== bNeg) return bNeg - aNeg;
      return (b.teachers_cy ?? 0) - (a.teachers_cy ?? 0);
    })
    .slice(0, 12);

  return sorted.map((block) => {
    const status = tqiStatus(block);
    return {
      block: block.block_name,
      strength: formatTeacherCompact(block.teachers_cy),
      growth: fmtSignedPct(block.teacher_growth_pct),
      growthPositive: (block.teacher_growth_pct ?? 0) >= 0,
      avgSchool: String(block.avg_teachers_per_school ?? 0),
      tqiStatus: status.label,
      tqiClass: status.className,
      risk: riskLabel(block),
      followUp: followUp(block),
    };
  });
};

export const buildNextReviewNote = () =>
  "Create a block-wise teacher quality heatmap in the Quality & Training tab and track progress weekly until CTET coverage crosses the next milestone.";

export const buildTeacherExecutiveMarkdown = (overview = {}, blockData = []) => {
  const strip = buildTeacherInsightStrip(overview, []);
  const actions = buildExecutiveActionQueue(overview, blockData);
  return [
    "## Insights",
    `- ${buildTeacherHeroHeadline(overview)}`,
    `- ${buildTeacherHeroNarrative(overview)}`,
    ...strip.map((s) => `- ${s.title}: ${s.body}`),
    "",
    "## Recommendations",
    ...actions.map((a) => `- ${a.title}: ${a.body}`),
    "",
    "## Priority Action Items",
    `- ${buildTeacherPrimaryAction(overview, blockData)}`,
    `- ${buildNextReviewNote()}`,
  ].join("\n");
};
